import { useEffect, useMemo, useRef, useState } from "react";
import { RefreshCw, Search, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, qs } from "@/lib/api";
import { Card, EmptyState, SectionTitle } from "@/components/Kpi";
import { NewsCard, NewsRiga, dataOra } from "@/components/NewsCard";
import NewsGestione from "@/components/NewsGestione";
import { NewsAuto } from "@/components/NewsAuto";
import { BoxIrs } from "@/components/BoxIrs";
import { useAuth } from "@/context/AuthContext";

const PERIODI = [["24h", "Ultime 24 ore"], ["7g", "7 giorni"], ["30g", "30 giorni"],
  ["3m", "3 mesi"], ["12m", "12 mesi"]];
const STATO_KEY = "dbe-news-stato";
const selCls = "border border-slate-200 rounded-md px-3 py-2 text-sm bg-white text-slate-700 " +
  "focus:outline-none focus:ring-2 focus:ring-sky-300 transition-colors duration-200";

export default function News() {
  const { user } = useAuth();
  const admin = ["MASTER", "ADMIN"].includes(user?.role);
  const salvato = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem(STATO_KEY)) || {}; } catch { return {}; }
  }, []);
  const [f, setF] = useState({ periodo: "3m", categoria: "", competitor: "", fonte: "",
    q: "", ordina: "data", impatto: "", ...(salvato.filtri || {}) });
  const [ricerca, setRicerca] = useState(f.q || "");
  const [opz, setOpz] = useState({ categorie: [], competitor: [], fonti: [] });
  const [dati, setDati] = useState({ rows: [], totale: 0, aggiornato_il: null });
  const [watch, setWatch] = useState([]);
  const [evidenza, setEvidenza] = useState([]);
  const [basso, setBasso] = useState({ rows: [], totale: 0 });
  const [limitBasso, setLimitBasso] = useState(8);
  const [limit, setLimit] = useState(salvato.limit || 10);
  const [caricando, setCaricando] = useState(true);
  const [gestione, setGestione] = useState(false);
  const [aggiornando, setAggiornando] = useState(false);
  const ripristinato = useRef(false);

  useEffect(() => {
    api.get("/news/opzioni").then((r) => setOpz(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setCaricando(true);
    api.get(`/news/feed?${qs({ ...f, sezione: "principali", limit })}`)
      .then((r) => setDati(r.data)).catch(() => setDati({ rows: [], totale: 0 }))
      .finally(() => setCaricando(false));
    if (f.impatto && f.impatto !== "Alto") setEvidenza([]);
    else {
      api.get(`/news/feed?${qs({ ...f, sezione: "principali", periodo: "30g", impatto: "Alto",
        ordina: "data", limit: 6 })}`)
        .then((r) => setEvidenza(r.data.rows || [])).catch(() => setEvidenza([]));
    }
    api.get(`/news/competitor-watch?${qs({ periodo: f.periodo, competitor: f.competitor })}`)
      .then((r) => setWatch(r.data.rows || [])).catch(() => setWatch([]));
  }, [JSON.stringify(f), limit]);

  useEffect(() => {
    if (f.impatto) { setBasso({ rows: [], totale: 0 }); return; }
    api.get(`/news/feed?${qs({ ...f, sezione: "secondarie", ordina: "data", limit: limitBasso })}`)
      .then((r) => setBasso(r.data)).catch(() => setBasso({ rows: [], totale: 0 }));
  }, [JSON.stringify(f), limitBasso]);

  useEffect(() => {
    sessionStorage.setItem(STATO_KEY, JSON.stringify({ filtri: f, limit,
      scroll: salvato.scroll }));
  }, [JSON.stringify(f), limit]);

  // ritorno dall'articolo: ripristina la posizione di scorrimento
  useEffect(() => {
    if (ripristinato.current || caricando || !salvato.scroll) return;
    ripristinato.current = true;
    setTimeout(() => window.scrollTo({ top: salvato.scroll }), 60);
  }, [caricando, salvato.scroll]);

  useEffect(() => {
    const salva = () => {
      const s = JSON.parse(sessionStorage.getItem(STATO_KEY) || "{}");
      sessionStorage.setItem(STATO_KEY, JSON.stringify({ ...s, scroll: window.scrollY }));
    };
    window.addEventListener("beforeunload", salva);
    return () => { salva(); window.removeEventListener("beforeunload", salva); };
  }, []);

  const inEvidenza = evidenza.map((a) => a.slug);
  const tutte = dati.rows.filter((a) => !inEvidenza.includes(a.slug));
  const competitorNomi = useMemo(
    () => Object.fromEntries((opz.competitor || []).map((c) => [c.key, c.nome])), [opz]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const cerca = (e) => { e.preventDefault(); setF({ ...f, q: ricerca }); };

  const aggiorna = async () => {
    setAggiornando(true);
    try {
      const r = await api.post("/news/admin/refresh");
      toast.success(r.data.messaggio);
      setTimeout(() => setF({ ...f }), 25000);
    } catch (e) { toast.error(apiError(e)); }
    setAggiornando(false);
  };

  return (
    <div data-testid="news-page">
      <SectionTitle sub={`Market intelligence · credito al consumo, finanziamenti finalizzati e automotive Italia${
        dati.aggiornato_il ? ` · ultimo aggiornamento ${dataOra(dati.aggiornato_il)}` : ""}`}
        right={admin && (
          <div className="flex gap-2">
            <button onClick={aggiorna} disabled={aggiornando} data-testid="news-aggiorna"
              className="inline-flex items-center gap-2 border-2 border-slate-900 text-slate-900 rounded-md
                px-3 py-2 text-sm hover:bg-slate-900 hover:text-white disabled:opacity-50
                transition-colors duration-200">
              <RefreshCw size={14} className={aggiornando ? "animate-spin" : ""} /> Aggiorna ora
            </button>
            <button onClick={() => setGestione(true)} data-testid="news-gestione"
              className="inline-flex items-center gap-2 bg-slate-900 text-white rounded-md px-3 py-2 text-sm
                hover:bg-slate-800 transition-colors duration-200">
              <Settings2 size={14} /> Gestione
            </button>
          </div>
        )}>
        News
      </SectionTitle>

      <BoxIrs admin={admin} />

      <Card className="p-4 mb-6 sm:sticky sm:top-0 z-20" data-testid="news-filtri">
        <div className="flex flex-wrap gap-2 items-center">
          <select className={selCls} value={f.periodo} onChange={set("periodo")} data-testid="filtro-periodo">
            {PERIODI.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select className={selCls} value={f.categoria} onChange={set("categoria")} data-testid="filtro-categoria">
            <option value="">Tutti gli argomenti</option>
            {(opz.categorie || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <select className={selCls} value={f.competitor} onChange={set("competitor")} data-testid="filtro-competitor">
            <option value="">Tutti i competitor</option>
            {(opz.competitor || []).map((c) => <option key={c.key} value={c.key}>{c.nome}</option>)}
          </select>
          <select className={selCls} value={f.fonte} onChange={set("fonte")} data-testid="filtro-fonte">
            <option value="">Tutte le fonti</option>
            {(opz.fonti || []).map((c) => <option key={c.key} value={c.key}>{c.nome}</option>)}
          </select>
          <select className={selCls} value={f.impatto} onChange={set("impatto")} data-testid="filtro-impatto">
            <option value="">Tutti gli impatti</option>
            <option value="Alto">Impatto alto</option>
            <option value="Medio">Impatto medio</option>
            <option value="Basso">Impatto basso</option>
          </select>
          <select className={selCls} value={f.ordina} onChange={set("ordina")} data-testid="filtro-ordina">
            <option value="data">Più recenti</option>
            <option value="rilevanza">Più rilevanti</option>
          </select>
          <form onSubmit={cerca} className="flex-1 min-w-[220px] relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className={`${selCls} w-full pl-9`} value={ricerca} data-testid="news-ricerca"
              placeholder="Cerca nelle notizie…" onChange={(e) => setRicerca(e.target.value)} />
          </form>
          {(f.categoria || f.competitor || f.fonte || f.q || f.impatto) && (
            <button data-testid="news-azzera"
              onClick={() => { setRicerca(""); setF({ periodo: f.periodo, categoria: "", competitor: "",
                fonte: "", q: "", impatto: "", ordina: f.ordina }); }}
              className="text-sm text-sky-700 hover:text-sky-900 transition-colors duration-200">
              Azzera filtri
            </button>
          )}
        </div>
      </Card>

      {evidenza.length > 0 && (
        <div className="mb-10">
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-3">
            In evidenza <span className="text-slate-400 normal-case tracking-normal">
              (alto impatto · ultimi 30 giorni)</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="news-evidenza">
            {evidenza.map((a) => <NewsCard key={a.slug} a={a} competitorNomi={competitorNomi} />)}
          </div>
        </div>
      )}

      <NewsAuto periodo={f.periodo} competitorNomi={competitorNomi} />

      <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-3">
        Tutte le notizie <span className="text-slate-400 normal-case tracking-normal">
          ({dati.totale} risultati)</span>
      </h3>
      {caricando && !dati.rows.length ? (
        <Card className="p-10 text-center text-sm text-slate-500">Caricamento notizie…</Card>
      ) : tutte.length === 0 && evidenza.length === 0 ? (
        <EmptyState title="Nessuna notizia per i filtri selezionati"
          description="Prova ad ampliare il periodo o ad azzerare i filtri. Il sistema aggiorna i contenuti due volte al giorno." />
      ) : (
        <>
          <div className="space-y-2" data-testid="news-lista">
            {tutte.map((a) => <NewsRiga key={a.slug} a={a} competitorNomi={competitorNomi} />)}
          </div>
          {dati.rows.length < dati.totale && (
            <div className="mt-4 text-center">
              <button onClick={() => setLimit(limit + 10)} data-testid="news-altre"
                className="border-2 border-slate-900 text-slate-900 rounded-md px-4 py-2 text-sm
                  hover:bg-slate-900 hover:text-white transition-colors duration-200">
                Mostra altre notizie ({dati.totale - dati.rows.length})
              </button>
            </div>
          )}
        </>
      )}

      <div className="mt-10">
        <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-3">
          Competitor watch
        </h3>
        {watch.length === 0 ? (
          <Card className="p-6 text-sm text-slate-500">
            Nessun aggiornamento sui competitor monitorati nel periodo selezionato.
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="competitor-watch">
            {watch.map((a) => <NewsCard key={a.slug} a={a} competitorNomi={competitorNomi} compact />)}
          </div>
        )}
      </div>

      {basso.rows.length > 0 && (
        <div className="mt-10">
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-3">
            Altre notizie <span className="text-slate-400 normal-case tracking-normal">
              (impatto basso e testate locali fuori area · {basso.totale})</span>
          </h3>
          <div className="space-y-1.5" data-testid="news-altre-lista">
            {basso.rows.map((a) => (
              <NewsRiga key={a.slug} a={a} competitorNomi={competitorNomi} compatta />
            ))}
          </div>
          {basso.rows.length < basso.totale && (
            <div className="mt-3 text-center">
              <button onClick={() => setLimitBasso(limitBasso + 10)} data-testid="news-altre-mostra"
                className="text-sm text-sky-700 hover:text-sky-900 transition-colors duration-200">
                Mostra altre
              </button>
            </div>
          )}
        </div>
      )}

      {gestione && <NewsGestione onClose={() => setGestione(false)} />}
    </div>
  );
}
